import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Italic,
  List,
  ListOrdered,
  Underline,
} from "lucide-react";
import { PD } from "@/constants/testIds";
import { RTF_FONT_SIZES } from "@/lib/pageDesignerRtf";
import { runRtfCommand } from "@/lib/pageDesignerRtfEditing";

function ToolbarButton({ title, testId, disabled, onClick, children }) {
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      data-testid={testId}
      disabled={disabled}
      className="epd__rtf-btn"
      onMouseDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!disabled) onClick?.();
      }}
    >
      {children}
    </button>
  );
}

export default function PageDesignerRtfToolbar({ cardId, enabled }) {
  const disabled = !enabled || !cardId;
  const run = (command, arg) => {
    if (disabled) return;
    runRtfCommand(cardId, command, arg);
  };

  return (
    <div
      className={`epd__rtf-toolbar${disabled ? " is-disabled" : ""}`}
      data-testid={PD.rtfToolbar}
      data-active-card={disabled ? "" : cardId}
      aria-label="RTF formatting"
      aria-disabled={disabled}
    >
      <span className="epd__rtf-label">RTF</span>
      <ToolbarButton title="Bold" testId={PD.rtfBold} disabled={disabled} onClick={() => run("bold")}>
        <Bold className="epd__rtf-icon" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton title="Italic" testId={PD.rtfItalic} disabled={disabled} onClick={() => run("italic")}>
        <Italic className="epd__rtf-icon" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        title="Underline"
        testId={PD.rtfUnderline}
        disabled={disabled}
        onClick={() => run("underline")}
      >
        <Underline className="epd__rtf-icon" aria-hidden="true" />
      </ToolbarButton>
      <span className="epd__rtf-sep" aria-hidden="true" />
      <select
        className="epd__rtf-select browser-default"
        aria-label="Font size"
        data-testid={PD.rtfFontSize}
        defaultValue="3"
        disabled={disabled}
        onMouseDown={(event) => event.stopPropagation()}
        onChange={(event) => run("fontSize", event.target.value)}
      >
        {RTF_FONT_SIZES.map((size) => (
          <option key={size.value} value={size.value}>
            {size.label}
          </option>
        ))}
      </select>
      <span className="epd__rtf-sep" aria-hidden="true" />
      <ToolbarButton
        title="Align left"
        testId={PD.rtfAlignLeft}
        disabled={disabled}
        onClick={() => run("justifyLeft")}
      >
        <AlignLeft className="epd__rtf-icon" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        title="Align center"
        testId={PD.rtfAlignCenter}
        disabled={disabled}
        onClick={() => run("justifyCenter")}
      >
        <AlignCenter className="epd__rtf-icon" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        title="Align right"
        testId={PD.rtfAlignRight}
        disabled={disabled}
        onClick={() => run("justifyRight")}
      >
        <AlignRight className="epd__rtf-icon" aria-hidden="true" />
      </ToolbarButton>
      <span className="epd__rtf-sep" aria-hidden="true" />
      <ToolbarButton
        title="Bulleted list"
        testId={PD.rtfBullet}
        disabled={disabled}
        onClick={() => run("insertUnorderedList")}
      >
        <List className="epd__rtf-icon" aria-hidden="true" />
      </ToolbarButton>
      <ToolbarButton
        title="Numbered list"
        testId={PD.rtfNumber}
        disabled={disabled}
        onClick={() => run("insertOrderedList")}
      >
        <ListOrdered className="epd__rtf-icon" aria-hidden="true" />
      </ToolbarButton>
    </div>
  );
}
